import React, { useEffect, useState, useMemo } from 'react';
import { MapContainer, TileLayer, Marker, Polyline, useMap } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { Loader2, Navigation, AlertCircle } from 'lucide-react';
import { haversineKm, openGoogleMapsNav } from '../lib/navigateTo';

/**
 * RoutePreview — in-app preview of the driver's route to a customer.
 *
 * Shows:
 *  - Customer destination pin (red, sharp)
 *  - Driver's live position (green, blinks every watchPosition update)
 *  - Driving route polyline + ETA + distance from OSRM (free, no key)
 *  - Big "Navigate" button that deep-links into Google Maps for the
 *    actual turn-by-turn (with traffic + voice + lane assist)
 *
 * OSRM route fetch: uses the public demo server
 * `router.project-osrm.org`. Free for low-volume non-commercial use;
 * production-grade traffic should self-host or proxy to a paid
 * provider eventually, but for "show driver the route line + ETA"
 * the demo is perfect. We never block UI on it — if the fetch fails
 * we fall back to a straight Haversine line + distance.
 *
 * Live driver location: we run our own watchPosition inside this
 * component (separate from the dashboard's network-streaming watch)
 * so the map updates instantly without waiting on a server round-trip.
 *
 * @param {{
 *   destination: { lat:number, lng:number, label?:string },
 *   compact?: boolean,
 * }} props
 */
const RoutePreview = ({ destination, compact = false }) => {
  const [driverPos, setDriverPos] = useState(null);
  const [geoError, setGeoError] = useState(null);
  const [route, setRoute] = useState(null); // { coords, distance, duration }
  const [routeLoading, setRouteLoading] = useState(false);

  // --- Live driver geolocation ---
  useEffect(() => {
    if (!navigator.geolocation) {
      setGeoError('Geolocation not available');
      return undefined;
    }
    const wid = navigator.geolocation.watchPosition(
      (pos) => {
        setGeoError(null);
        setDriverPos({ lat: pos.coords.latitude, lng: pos.coords.longitude });
      },
      (err) => setGeoError(err?.message || 'Location blocked'),
      { enableHighAccuracy: true, timeout: 15000, maximumAge: 5000 },
    );
    return () => navigator.geolocation.clearWatch(wid);
  }, []);

  // --- Route from OSRM ---
  // Re-fetch when driver moves >250m (rough debounce so we don't
  // hammer the OSRM demo server). 250m chosen because Oman streets are
  // wide and route geometry rarely changes for shorter movement.
  const driverKey = useMemo(() => {
    if (!driverPos) return null;
    // Round to ~250m precision (~0.003° lat).
    return `${driverPos.lat.toFixed(3)},${driverPos.lng.toFixed(3)}`;
  }, [driverPos]);

  useEffect(() => {
    if (!driverPos || !destination) return undefined;
    let cancelled = false;
    setRouteLoading(true);
    const url =
      `https://router.project-osrm.org/route/v1/driving/` +
      `${driverPos.lng},${driverPos.lat};${destination.lng},${destination.lat}` +
      `?overview=full&geometries=geojson`;
    fetch(url)
      .then((r) => (r.ok ? r.json() : Promise.reject(new Error(`OSRM HTTP ${r.status}`))))
      .then((data) => {
        if (cancelled) return;
        const r = data?.routes?.[0];
        if (!r) {
          setRoute(null);
          return;
        }
        const coords = (r.geometry?.coordinates || []).map(([lng, lat]) => [lat, lng]);
        setRoute({
          coords,
          distance: r.distance, // meters
          duration: r.duration, // seconds
        });
      })
      .catch(() => {
        if (!cancelled) setRoute(null);
      })
      .finally(() => {
        if (!cancelled) setRouteLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [driverKey, destination?.lat, destination?.lng, destination, driverPos]);

  // --- Icons ---
  const destIcon = useMemo(
    () =>
      L.divIcon({
        className: 'mr-route-pin-dest',
        html: `
          <div style="
            width:34px;height:42px;
            display:flex;align-items:flex-start;justify-content:center;
            filter:drop-shadow(0 4px 6px rgba(0,0,0,.35));
          ">
            <svg viewBox="0 0 24 32" width="34" height="42">
              <path d="M12 0C5.4 0 0 5.4 0 12c0 8.8 12 20 12 20s12-11.2 12-20C24 5.4 18.6 0 12 0z"
                fill="#dc2626"/>
              <circle cx="12" cy="12" r="5" fill="white"/>
            </svg>
          </div>`,
        iconSize: [34, 42],
        iconAnchor: [17, 42],
      }),
    [],
  );

  const driverIcon = useMemo(
    () =>
      L.divIcon({
        className: 'mr-route-pin-driver',
        html: `
          <div style="
            width:24px;height:24px;border-radius:50%;
            background:#10b981;border:3px solid white;
            box-shadow:0 0 0 4px rgba(16,185,129,.35), 0 2px 6px rgba(0,0,0,.4);
          "></div>`,
        iconSize: [24, 24],
        iconAnchor: [12, 12],
      }),
    [],
  );

  // --- Distance / ETA display ---
  const display = useMemo(() => {
    if (route) {
      const km = route.distance / 1000;
      const mins = Math.max(1, Math.round(route.duration / 60));
      return {
        distance: km < 1 ? `${Math.round(km * 1000)} m` : `${km.toFixed(1)} km`,
        eta: mins < 60 ? `${mins} min` : `${Math.floor(mins / 60)}h ${mins % 60}m`,
        source: 'osrm',
      };
    }
    if (driverPos && destination) {
      const km = haversineKm(driverPos, destination);
      if (km !== null) {
        return {
          distance: km < 1 ? `${Math.round(km * 1000)} m` : `${km.toFixed(1)} km`,
          eta: null,
          source: 'estimate',
        };
      }
    }
    return null;
  }, [route, driverPos, destination]);

  const center = useMemo(() => {
    if (driverPos && destination) {
      return [(driverPos.lat + destination.lat) / 2, (driverPos.lng + destination.lng) / 2];
    }
    if (destination) return [destination.lat, destination.lng];
    return [23.5859, 58.4059]; // Muscat fallback
  }, [driverPos, destination]);

  if (!destination?.lat || !destination?.lng) {
    return (
      <div className="rounded-xl bg-amber-500/10 border border-amber-500/30 p-3 text-amber-200 text-xs flex items-start gap-2">
        <AlertCircle className="w-4 h-4 flex-shrink-0 mt-0.5" />
        <span>No customer GPS location saved for this order.</span>
      </div>
    );
  }

  const mapHeight = compact ? 'h-44' : 'h-64';

  return (
    <div className="rounded-xl overflow-hidden border border-white/10 bg-black/30">
      <div className={`${mapHeight} relative`}>
        <MapContainer
          center={center}
          zoom={13}
          className="h-full w-full"
          scrollWheelZoom={false}
          style={{ background: '#0b1220' }}
          data-testid="route-preview-map"
        >
          <TileLayer
            attribution='&copy; OpenStreetMap'
            url="https://{s}.tile.openstreetmap.org/{z}/{y}/{x}.png"
          />
          <Marker position={[destination.lat, destination.lng]} icon={destIcon} />
          {driverPos && <Marker position={[driverPos.lat, driverPos.lng]} icon={driverIcon} />}
          {route?.coords?.length > 1 && (
            <Polyline
              positions={route.coords}
              pathOptions={{ color: '#10b981', weight: 5, opacity: 0.85 }}
            />
          )}
          {!route && driverPos && (
            <Polyline
              positions={[[driverPos.lat, driverPos.lng], [destination.lat, destination.lng]]}
              pathOptions={{
                color: '#10b981',
                weight: 3,
                opacity: 0.6,
                dashArray: '6, 8',
              }}
            />
          )}
          <FitBounds driverPos={driverPos} destination={destination} route={route} />
        </MapContainer>

        {/* Loading / status badge */}
        {routeLoading && (
          <div className="absolute top-2 right-2 bg-black/70 backdrop-blur-sm rounded-full px-2.5 py-1 text-[10px] font-bold text-white flex items-center gap-1.5 z-[400]">
            <Loader2 className="w-3 h-3 animate-spin" /> Routing…
          </div>
        )}
      </div>

      {/* Footer: distance + ETA + Navigate */}
      <div className="px-3 py-2.5 flex items-center gap-3 bg-[#0e1525]">
        {display ? (
          <div className="flex-1 min-w-0">
            <div className="text-[10px] font-bold tracking-wider text-slate-400 uppercase">
              {display.source === 'osrm' ? 'Driving distance' : 'Straight-line estimate'}
            </div>
            <div className="text-sm text-white font-semibold leading-tight">
              {display.distance}
              {display.eta && <span className="text-emerald-300 font-bold"> · {display.eta}</span>}
            </div>
          </div>
        ) : (
          <div className="flex-1 min-w-0 text-xs text-slate-400 flex items-center gap-1.5">
            {geoError ? (
              <>
                <AlertCircle className="w-3.5 h-3.5 text-amber-400" />
                {geoError}
              </>
            ) : (
              <>
                <Loader2 className="w-3.5 h-3.5 animate-spin" /> Getting your location…
              </>
            )}
          </div>
        )}
        <button
          type="button"
          onClick={() => openGoogleMapsNav(destination)}
          className="rounded-full bg-emerald-500 hover:bg-emerald-600 text-white text-xs font-bold px-4 py-2 inline-flex items-center gap-1.5 flex-shrink-0 transition active:scale-95"
          data-testid="route-preview-navigate-btn"
        >
          <Navigation className="w-3.5 h-3.5" /> Navigate
        </button>
      </div>
    </div>
  );
};

/**
 * Auto-fit the map bounds to driver + destination + route (whenever
 * any of them change). Lives inside MapContainer because useMap()
 * only works for children of the map.
 */
const FitBounds = ({ driverPos, destination, route }) => {
  const map = useMap();
  useEffect(() => {
    if (!map) return;
    const pts = [];
    if (driverPos) pts.push([driverPos.lat, driverPos.lng]);
    if (destination) pts.push([destination.lat, destination.lng]);
    if (route?.coords?.length) {
      route.coords.forEach((c) => pts.push(c));
    }
    if (pts.length < 2) {
      if (pts.length === 1) map.setView(pts[0], 15);
      return;
    }
    const bounds = L.latLngBounds(pts);
    map.fitBounds(bounds, { padding: [40, 40], maxZoom: 16 });
  }, [map, driverPos?.lat, driverPos?.lng, destination?.lat, destination?.lng, route, driverPos, destination]);
  return null;
};

export default RoutePreview;
